import { Button, CircularProgress } from '@material-ui/core';
import ModuleContext from 'CMFW/context/ModuleContext';
import { booleanCheckRenderer } from 'CMFW/grid/cellRenderers/booleanCheck';
import { IFields } from 'CMFW/grid/fields';
import Grid from 'CMFW/grid/Grid';
import ItemLayout from 'CMFW/layout/ItemLayout';
import { LoadingItem } from 'CMFW/layout/Loading';
import { ContainerLeft, ContainerRight, EditorGap, EditorRow, ModuleContainer } from 'components/common/styles';
import { Split } from 'components/Split';
import { useForceRender } from 'hooks/useForceRender';
import HanziModel from 'models/HanziModel';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import HanziCardItem from './HanziCardItem';

const cellRenderers = {
  [IFields.hasCard]: booleanCheckRenderer,
  [IFields.complete]: booleanCheckRenderer,
  [IFields.componentOnly]: booleanCheckRenderer,
};

/**
 * Two steps: scan the ZH targets (vocabulary, items, compounds, cultural vocabulary) and list
 * every character with or without card; then build the missing ones with their closure of
 * components. The dry run reports what would be built without writing.
 */
const HanziWizardTab: React.FC = () => {
  const forceRender = useForceRender();
  const model = useMemo(() => new HanziModel({ render: forceRender, mode: 'wizard' }), [forceRender]);
  const scan = model.scan;
  const report = model.report;
  const missing = model.missingChars().length;
  const busy = model.loadingGrid || model.applying;

  return (
    <ModuleContainer>
      <ModuleContext.Provider value={model}>
        <Split widthLeft={620}>
          <ContainerLeft>
            <EditorRowLeft>
              <Button variant="outlined" size="small" onClick={() => model.getGrid({})} disabled={busy}>
                {model.loadingGrid ? <CircularProgress size={16} /> : '1 · scan targets (ZH)'}
              </Button>
              <EditorGap />
              <Button
                variant="outlined"
                size="small"
                onClick={() => model.apply(false)}
                disabled={busy || missing === 0}
              >
                2 · preview (dry run)
              </Button>
              <EditorGap />
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={() => model.apply(true)}
                disabled={busy || missing === 0}
              >
                {model.applying ? <CircularProgress size={16} /> : `2 · create ${missing} missing cards`}
              </Button>
            </EditorRowLeft>
            {scan && (
              <EditorRow>
                <Info>
                  {scan.summary.total} characters in targets · {scan.summary.ok} ok · {scan.summary.missing} without
                  card · {scan.summary.incomplete} incomplete
                </Info>
              </EditorRow>
            )}
            {report && (
              <EditorRow>
                <Info>
                  {report.applied ? 'Applied' : 'Dry run'}: {report.requested} requested · {report.closure} with closure
                  · {report.built} built · {report.synthetic} synthetic · {report.skippedExisting} already there
                  {report.noData.length > 0 && ` · no data: ${report.noData.join(' ')}`}
                  {report.incomplete.length > 0 && ` · incomplete: ${report.incomplete.join(' ')}`}
                  {report.syntheticWithoutHost.length > 0 && ` · no host: ${report.syntheticWithoutHost.join(' ')}`}
                </Info>
              </EditorRow>
            )}
            <Grid cellRenderers={cellRenderers} />
          </ContainerLeft>
          <ContainerRight>
            <ItemLayout>
              <HanziCardItem />
            </ItemLayout>
            <LoadingItem />
          </ContainerRight>
        </Split>
      </ModuleContext.Provider>
    </ModuleContainer>
  );
};

const EditorRowLeft = styled(EditorRow)`
  justify-content: flex-start;
`;

const Info = styled.span`
  font-size: 12px;
  color: #757575;
`;

export default HanziWizardTab;
